// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file banking.service.ts
// @author Arlinda Peixoto
// @company Pagali
// @description Serviço de gestão de Banking para Admin
// @date 2026-01-28
// ------------------------------------------------------------

import api from './api';

// ============================================
// TIPOS - TRANSFERÊNCIAS
// ============================================

export interface Transfer {
  id: string;
  type: 'p2p' | 'bank' | 'international';
  senderId: string;
  senderName: string;
  senderPhone?: string;
  recipientId?: string;
  recipientName: string;
  recipientPhone?: string;
  recipientBankCode?: string;
  recipientAccountNumber?: string;
  amount: number;
  fee: number;
  currency: string;
  exchangeRate?: number;
  destinationAmount?: number;
  destinationCurrency?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  description?: string;
  corridor?: string;
  createdAt: string;
  completedAt?: string;
  failureReason?: string;
}

export interface TransferFilters {
  search?: string;
  type?: string;
  status?: string;
  senderId?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
}

export interface TransfersResponse {
  success: boolean;
  data: Transfer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalTransfers: number;
    totalAmount: number;
    totalFees: number;
    byType: { type: string; count: number; amount: number }[];
    byStatus: { status: string; count: number }[];
  };
}

// ============================================
// TIPOS - PAGAMENTO DE FATURAS
// ============================================

export interface BillPayment {
  id: string;
  customerId: string;
  customerName: string;
  providerId: number;
  providerName: string;
  providerCategory: string;
  reference: string;
  amount: number;
  fee: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  completedAt?: string;
  transactionId?: string;
}

export interface ServiceProvider {
  id: number;
  name: string;
  category: string;
  logo?: string;
  referenceFormat: string;
  referenceLength: number;
  minAmount?: number;
  maxAmount?: number;
  fee: number;
  feeType: 'fixed' | 'percentage';
  active: boolean;
  totalTransactions: number;
  totalAmount: number;
}

export interface BillPaymentFilters {
  search?: string;
  providerId?: number;
  category?: string;
  status?: string;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface BillPaymentsResponse {
  success: boolean;
  data: BillPayment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalPayments: number;
    totalAmount: number;
    totalFees: number;
    byCategory: { category: string; count: number; amount: number }[];
    byProvider: { providerId: number; providerName: string; count: number; amount: number }[];
  };
}

// ============================================
// TIPOS - CRÉDITO
// ============================================

export interface CreditApplication {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  productId: string;
  productName: string;
  productType: 'personal_loan' | 'credit_line' | 'overdraft' | 'microcredit';
  requestedAmount: number;
  approvedAmount?: number;
  term: number;
  interestRate: number;
  monthlyPayment?: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'cancelled' | 'disbursed';
  kycLevel: string;
  creditScore?: number;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  notes?: string;
}

export interface CreditContract {
  id: string;
  applicationId: string;
  customerId: string;
  customerName: string;
  productName: string;
  principalAmount: number;
  outstandingBalance: number;
  interestRate: number;
  monthlyPayment: number;
  term: number;
  paidInstallments: number;
  remainingInstallments: number;
  nextPaymentDate: string;
  nextPaymentAmount: number;
  status: 'active' | 'paid_off' | 'overdue' | 'defaulted' | 'restructured';
  daysOverdue?: number;
  totalPaid: number;
  totalInterestPaid: number;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface CreditFilters {
  search?: string;
  status?: string;
  productType?: string;
  customerId?: string;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreditApplicationsResponse {
  success: boolean;
  data: CreditApplication[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    totalRequestedAmount: number;
    totalApprovedAmount: number;
  };
}

export interface CreditContractsResponse {
  success: boolean;
  data: CreditContract[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalContracts: number;
    activeContracts: number;
    overdueContracts: number;
    totalOutstanding: number;
    totalPaid: number;
  };
}

// ============================================
// TIPOS - KYC
// ============================================

export interface KycVerification {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  currentLevel: 'basic' | 'standard' | 'premium' | 'business';
  requestedLevel: 'standard' | 'premium' | 'business';
  status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'requires_info';
  documents: KycDocument[];
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  notes?: string;
}

export interface KycDocument {
  id: string;
  type: 'id_front' | 'id_back' | 'passport' | 'selfie' | 'proof_of_address' | 'proof_of_income';
  fileName: string;
  fileUrl: string;
  status: 'pending' | 'verified' | 'rejected';
  uploadedAt: string;
  verifiedAt?: string;
  rejectionReason?: string;
}

export interface KycFilters {
  search?: string;
  status?: string;
  requestedLevel?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface KycResponse {
  success: boolean;
  data: KycVerification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    total: number;
    pending: number;
    inReview: number;
    approved: number;
    rejected: number;
  };
}

// ============================================
// TIPOS - ANALYTICS
// ============================================

export interface BankingAnalytics {
  period: string;
  transfers: {
    total: number;
    count: number;
    byType: {
      p2p: { amount: number; count: number };
      bank: { amount: number; count: number };
      international: { amount: number; count: number };
    };
    byStatus: {
      completed: number;
      pending: number;
      failed: number;
    };
    growth: number;
  };
  billPayments: {
    total: number;
    count: number;
    byCategory: { category: string; count: number; amount: number }[];
    growth: number;
  };
  credit: {
    totalDisbursed: number;
    activeContracts: number;
    applications: {
      pending: number;
      approved: number;
      rejected: number;
    };
    defaultRate: number;
    growth: number;
  };
  kyc: {
    total: number;
    byLevel: {
      basic: number;
      standard: number;
      premium: number;
      business: number;
    };
    pending: number;
    approvalRate: number;
  };
  timeline: {
    date: string;
    transfers: number;
    bills: number;
    credit: number;
  }[];
}

// ============================================
// SERVIÇO
// ============================================

class BankingService {
  // ============================================
  // TRANSFERÊNCIAS
  // ============================================

  async getTransfers(filters?: TransferFilters): Promise<TransfersResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.senderId) params.append('senderId', filters.senderId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/banking/transfers?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get transfers error:', error);
      throw error;
    }
  }

  async getTransfer(id: string): Promise<Transfer> {
    try {
      const response = await api.get(`/admin/banking/transfers/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get transfer error:', error);
      throw error;
    }
  }

  async refundTransfer(id: string, reason: string): Promise<void> {
    try {
      await api.post(`/admin/banking/transfers/${id}/refund`, { reason });
    } catch (error) {
      console.error('Refund transfer error:', error);
      throw error;
    }
  }

  async cancelTransfer(id: string, reason: string): Promise<void> {
    try {
      await api.post(`/admin/banking/transfers/${id}/cancel`, { reason });
    } catch (error) {
      console.error('Cancel transfer error:', error);
      throw error;
    }
  }

  // ============================================
  // PAGAMENTO DE FATURAS
  // ============================================

  async getBillPayments(filters?: BillPaymentFilters): Promise<BillPaymentsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.providerId) params.append('providerId', filters.providerId.toString());
      if (filters?.category) params.append('category', filters.category);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/banking/bills?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get bill payments error:', error);
      throw error;
    }
  }

  async getProviders(): Promise<ServiceProvider[]> {
    try {
      const response = await api.get('/admin/banking/bills/providers');
      return response.data.data;
    } catch (error) {
      console.error('Get providers error:', error);
      throw error;
    }
  }

  async createProvider(provider: Partial<ServiceProvider>): Promise<ServiceProvider> {
    try {
      const response = await api.post('/admin/banking/bills/providers', provider);
      return response.data.data;
    } catch (error) {
      console.error('Create provider error:', error);
      throw error;
    }
  }

  async updateProvider(id: number, provider: Partial<ServiceProvider>): Promise<ServiceProvider> {
    try {
      const response = await api.put(`/admin/banking/bills/providers/${id}`, provider);
      return response.data.data;
    } catch (error) {
      console.error('Update provider error:', error);
      throw error;
    }
  }

  async toggleProviderStatus(id: number): Promise<void> {
    try {
      await api.post(`/admin/banking/bills/providers/${id}/toggle`);
    } catch (error) {
      console.error('Toggle provider error:', error);
      throw error;
    }
  }

  // ============================================
  // CRÉDITO
  // ============================================

  async getCreditApplications(filters?: CreditFilters): Promise<CreditApplicationsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.productType) params.append('productType', filters.productType);
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/banking/credit/applications?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get credit applications error:', error);
      throw error;
    }
  }

  async getCreditApplication(id: string): Promise<CreditApplication> {
    try {
      const response = await api.get(`/admin/banking/credit/applications/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get credit application error:', error);
      throw error;
    }
  }

  async approveCreditApplication(id: string, approvedAmount: number, notes?: string): Promise<void> {
    try {
      await api.post(`/admin/banking/credit/applications/${id}/approve`, { approvedAmount, notes });
    } catch (error) {
      console.error('Approve credit error:', error);
      throw error;
    }
  }

  async rejectCreditApplication(id: string, reason: string): Promise<void> {
    try {
      await api.post(`/admin/banking/credit/applications/${id}/reject`, { reason });
    } catch (error) {
      console.error('Reject credit error:', error);
      throw error;
    }
  }

  async getCreditContracts(filters?: CreditFilters): Promise<CreditContractsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/banking/credit/contracts?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get credit contracts error:', error);
      throw error;
    }
  }

  async getCreditContract(id: string): Promise<CreditContract> {
    try {
      const response = await api.get(`/admin/banking/credit/contracts/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get credit contract error:', error);
      throw error;
    }
  }

  // ============================================
  // KYC
  // ============================================

  async getKycVerifications(filters?: KycFilters): Promise<KycResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.requestedLevel) params.append('requestedLevel', filters.requestedLevel);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/banking/kyc?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get KYC verifications error:', error);
      throw error;
    }
  }

  async getKycVerification(id: string): Promise<KycVerification> {
    try {
      const response = await api.get(`/admin/banking/kyc/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get KYC verification error:', error);
      throw error;
    }
  }

  async approveKyc(id: string, notes?: string): Promise<void> {
    try {
      await api.post(`/admin/banking/kyc/${id}/approve`, { notes });
    } catch (error) {
      console.error('Approve KYC error:', error);
      throw error;
    }
  }

  async rejectKyc(id: string, reason: string): Promise<void> {
    try {
      await api.post(`/admin/banking/kyc/${id}/reject`, { reason });
    } catch (error) {
      console.error('Reject KYC error:', error);
      throw error;
    }
  }

  async requestKycInfo(id: string, message: string): Promise<void> {
    try {
      await api.post(`/admin/banking/kyc/${id}/request-info`, { message });
    } catch (error) {
      console.error('Request KYC info error:', error);
      throw error;
    }
  }

  async verifyDocument(kycId: string, documentId: string): Promise<void> {
    try {
      await api.post(`/admin/banking/kyc/${kycId}/documents/${documentId}/verify`);
    } catch (error) {
      console.error('Verify document error:', error);
      throw error;
    }
  }

  async rejectDocument(kycId: string, documentId: string, reason: string): Promise<void> {
    try {
      await api.post(`/admin/banking/kyc/${kycId}/documents/${documentId}/reject`, { reason });
    } catch (error) {
      console.error('Reject document error:', error);
      throw error;
    }
  }

  // ============================================
  // ANALYTICS
  // ============================================

  async getAnalytics(period: string = 'month'): Promise<BankingAnalytics> {
    try {
      const response = await api.get(`/admin/banking/analytics?period=${period}`);
      return response.data.data;
    } catch (error) {
      console.error('Get analytics error:', error);
      throw error;
    }
  }

  async exportTransfers(filters?: TransferFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/admin/banking/transfers/export?${params.toString()}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Export transfers error:', error);
      throw error;
    }
  }

  async exportBillPayments(filters?: BillPaymentFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/admin/banking/bills/export?${params.toString()}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Export bill payments error:', error);
      throw error;
    }
  }

  async getCreditScores(filters?: { minScore?: number; maxScore?: number; grade?: string; limit?: number }): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (filters?.minScore) params.append('minScore', String(filters.minScore));
      if (filters?.maxScore) params.append('maxScore', String(filters.maxScore));
      if (filters?.grade) params.append('grade', filters.grade);
      if (filters?.limit) params.append('limit', String(filters.limit));

      const response = await api.get(`/client/banking/credit/admin/scores?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      // 503 Banking Hub desativado — retornar silenciosamente sem poluir o console
      const status = error?.response?.status;
      const msg: string = error?.response?.data?.error ?? error?.message ?? '';
      if (status === 503 || msg.includes('Banking Hub') || msg.includes('Banco Central')) {
        return { success: false, error: msg, bankingHubDisabled: true };
      }
      console.error('Get credit scores error:', error);
      throw error;
    }
  }

  async getInternationalTransfers(filters?: { status?: string; destCountry?: string; startDate?: string; endDate?: string }): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.destCountry) params.append('destCountry', filters.destCountry);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/client/banking/transfers/admin/international?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get international transfers error:', error);
      throw error;
    }
  }

  async exportData(type: 'transfers' | 'bills' | 'credit' | 'kyc', filters?: { period?: string }): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.period) params.append('period', filters.period);

      const response = await api.get(`/admin/banking/${type}/export?${params.toString()}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error(`Export ${type} error:`, error);
      throw error;
    }
  }
}

const bankingService = new BankingService();
export default bankingService;
